'use client'
import { type PropsWithChildren } from 'react'
import clsx from 'clsx'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { ImageWithPlaceholder } from '@/components/ImageWithPlaceholder/ImageWithPlaceholder'
import { type GridColumnsField, type SbBaseBlockProps, type StoryblokAsset } from '@/services/storyblok/storyblok'
import { getImgSrc } from '@/services/storyblok/Storyblok.helpers'
import { DEFAULT_IMAGE_QUALITY } from '@/utils/config'
import { storyblokEditable } from '@storyblok/react'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import { ConditionalWrapper } from 'ui/src/components/ConditionalWrapper'
import {
  aspectRatioLandscapeVar,
  aspectRatioPortraitVar,
  image,
  imageWrapper,
  roundedCornersImage,
} from './ImageBlock.css'

type ImageAspectRatio = '1 / 1' | '2 / 1' | '3 / 2' | '4 / 3' | '5 / 4' | '16 / 9'

export type BaseImageRendererProps = {
  image: StoryblokAsset
  fullBleed?: boolean
  priority?: boolean
  layout?: GridColumnsField
  aspectRatioLandscape?: ImageAspectRatio
  aspectRatioPortrait?: ImageAspectRatio
}
export type ImageBlockRendererProps = PropsWithChildren<SbBaseBlockProps<BaseImageRendererProps>>

export const ImageBlockRenderer = ({ blok, nested, children }: ImageBlockRendererProps) => {
  return (
    <ConditionalWrapper
      condition={!blok.fullBleed && !nested}
      wrapWith={(children) => (
        <GridLayout.Root>
          <GridLayout.Content width={blok.layout?.widths ?? { _: '1' }} align={blok.layout?.alignment ?? 'center'}>
            {children}
          </GridLayout.Content>
        </GridLayout.Root>
      )}
    >
      <div
        {...storyblokEditable(blok)}
        className={imageWrapper}
        style={assignInlineVars({
          [aspectRatioLandscapeVar]: blok.aspectRatioLandscape,
          [aspectRatioPortraitVar]: blok.aspectRatioPortrait,
        })}
      >
        <ImageWithPlaceholder
          className={clsx(image, !blok.fullBleed && roundedCornersImage)}
          src={getImgSrc(blok.image.filename)}
          alt={blok.image.alt}
          fill={true}
          imagePriority={blok.priority}
          quality={DEFAULT_IMAGE_QUALITY}
        />
        {children}
      </div>
    </ConditionalWrapper>
  )
}
